import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Assessment as MetricsIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Key as KeyIcon,
  ReceiptLong as StatementIcon,
  Webhook as WebhookIcon
} from '@mui/icons-material';
import axios from 'axios';

import { API, getAuthHeader as authHeader } from './customerApp/shared';

const EMPTY_FORM = {
  name: '',
  code: '',
  displayName: '',
  primaryColor: '#1976d2',
  secondaryColor: '#424242',
  supportPhone: '',
  supportEmail: '',
  autoDispatch: true,
  markupPercent: 0,
  goaFee: 0,
  cancellationFee: 0,
  contactName: '',
  contactEmail: '',
  contactPhone: ''
};

const formFromPartner = (partner) => ({
  name: partner.name || '',
  code: partner.code || '',
  displayName: partner.branding?.displayName || '',
  primaryColor: partner.branding?.primaryColor || '#1976d2',
  secondaryColor: partner.branding?.secondaryColor || '#424242',
  supportPhone: partner.branding?.supportPhone || '',
  supportEmail: partner.branding?.supportEmail || '',
  autoDispatch: partner.settings?.autoDispatch !== false,
  markupPercent: partner.billing?.markupPercent ?? 0,
  goaFee: partner.billing?.goaFee ?? 0,
  cancellationFee: partner.billing?.cancellationFee ?? 0,
  contactName: partner.contact?.name || '',
  contactEmail: partner.contact?.email || '',
  contactPhone: partner.contact?.phone || ''
});

const payloadFromForm = (form) => ({
  name: form.name,
  code: form.code,
  branding: {
    displayName: form.displayName,
    primaryColor: form.primaryColor,
    secondaryColor: form.secondaryColor,
    supportPhone: form.supportPhone,
    supportEmail: form.supportEmail
  },
  settings: { autoDispatch: form.autoDispatch },
  billing: {
    markupPercent: Number(form.markupPercent) || 0,
    goaFee: Number(form.goaFee) || 0,
    cancellationFee: Number(form.cancellationFee) || 0
  },
  contact: { name: form.contactName, email: form.contactEmail, phone: form.contactPhone }
});

/** White-label partner management (owners only). */
function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = creating
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Shown-once API key dialog
  const [issuedKey, setIssuedKey] = useState(null); // { partnerName, apiKey }

  // Metrics dialog
  const [metricsPartner, setMetricsPartner] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [statementMonth, setStatementMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM

  // Webhook dialog
  const [webhookPartner, setWebhookPartner] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState(null); // shown once
  const [webhookBusy, setWebhookBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/api/partners`, { headers: authHeader() });
      setPartners(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (partner) => { setEditingId(partner.id); setForm(formFromPartner(partner)); setDialogOpen(true); };
  const setField = (key) => (event) =>
    setForm((prev) => ({ ...prev, [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value }));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await axios.put(`${API}/api/partners/${editingId}`, payloadFromForm(form), { headers: authHeader() });
      } else {
        const { data } = await axios.post(`${API}/api/partners`, payloadFromForm(form), { headers: authHeader() });
        setIssuedKey({ partnerName: data.name, apiKey: data.apiKey });
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (partner) => {
    try {
      await axios.put(`${API}/api/partners/${partner.id}`, { active: !partner.active }, { headers: authHeader() });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed');
    }
  };

  const rotateKey = async (partner) => {
    if (!window.confirm(`Rotate the API key for ${partner.name}? The current key stops working immediately.`)) return;
    try {
      const { data } = await axios.post(`${API}/api/partners/${partner.id}/rotate-key`, {}, { headers: authHeader() });
      setIssuedKey({ partnerName: data.name, apiKey: data.apiKey });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Key rotation failed');
    }
  };

  const uploadLogo = async (partner, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('logo', file);
    try {
      await axios.post(`${API}/api/partners/${partner.id}/logo`, formData, { headers: authHeader() });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Logo upload failed');
    }
  };

  const openMetrics = async (partner) => {
    setMetricsPartner(partner);
    setMetrics(null);
    try {
      const from = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
      const { data } = await axios.get(
        `${API}/api/partners/${partner.id}/metrics?from=${encodeURIComponent(from)}`,
        { headers: authHeader() }
      );
      setMetrics(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load metrics');
    }
  };

  const downloadStatement = async (partner) => {
    const [year, month] = statementMonth.split('-').map(Number);
    try {
      const response = await axios.get(
        `${API}/api/partners/${partner.id}/statement?year=${year}&month=${month}&format=csv`,
        { headers: authHeader(), responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${partner.code}-statement-${statementMonth}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Statement download failed');
    }
  };

  const openWebhooks = async (partner) => {
    setWebhookPartner(partner);
    setWebhookUrl('');
    setWebhookSecret(null);
    try {
      const { data } = await axios.get(`${API}/api/integrations/webhooks`, { headers: authHeader() });
      setWebhooks(data.filter((hook) => hook.partnerId === partner.id));
    } catch {
      setWebhooks([]);
    }
  };

  const addWebhook = async () => {
    if (!webhookUrl || !webhookPartner) return;
    setWebhookBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/integrations/webhooks`,
        { name: `${webhookPartner.name} webhook`, url: webhookUrl, events: [], partnerId: webhookPartner.id },
        { headers: authHeader() }
      );
      setWebhookSecret(data.secret);
      setWebhookUrl('');
      setWebhooks((prev) => [...prev, data]);
    } catch (err) {
      setError(err.response?.data?.message || 'Webhook creation failed');
    } finally {
      setWebhookBusy(false);
    }
  };

  const removeWebhook = async (hook) => {
    if (!window.confirm(`Delete the webhook to ${hook.url}?`)) return;
    try {
      await axios.delete(`${API}/api/integrations/webhooks/${hook._id}`, { headers: authHeader() });
      setWebhooks((prev) => prev.filter((h) => h._id !== hook._id));
    } catch (err) {
      setError(err.response?.data?.message || 'Webhook deletion failed');
    }
  };

  const copyText = (text) => navigator.clipboard?.writeText(text).catch(() => {});

  return (
    <Box mt={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">White-Label Partners</Typography>
          <Typography variant="body2" color="text.secondary">
            Vehicle & insurance companies sending cases through the Partner API under their own brand
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Partner
        </Button>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Partner</TableCell>
                <TableCell>Customer-Facing Brand</TableCell>
                <TableCell>API Key</TableCell>
                <TableCell>Auto-Dispatch</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {partners.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" align="center" py={2}>
                      No partners yet. Add one to issue an API key and start receiving cases.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {partners.map((partner) => (
                <TableRow key={partner.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{partner.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{partner.code}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {partner.branding?.logoUrl ? (
                        <Box component="img" src={`${API}${partner.branding.logoUrl}`} alt="" sx={{ height: 24, maxWidth: 60, objectFit: 'contain' }} />
                      ) : (
                        <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: partner.branding?.primaryColor || '#1976d2' }} />
                      )}
                      <Typography variant="body2">{partner.branding?.displayName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">{partner.apiKeyPrefix}…</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={partner.settings?.autoDispatch !== false ? 'On' : 'Off'}
                      color={partner.settings?.autoDispatch !== false ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={partner.active ? 'Active' : 'Disabled'}
                      color={partner.active ? 'success' : 'default'}
                      onClick={() => toggleActive(partner)}
                      sx={{ cursor: 'pointer' }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit branding & settings">
                      <IconButton size="small" onClick={() => openEdit(partner)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Upload logo">
                      <IconButton size="small" component="label">
                        <ImageIcon fontSize="small" />
                        <input hidden type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          onChange={(event) => { uploadLogo(partner, event.target.files?.[0]); event.target.value = ''; }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="SLA scorecard & billing statement">
                      <IconButton size="small" onClick={() => openMetrics(partner)}><MetricsIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Webhooks (status events to the partner)">
                      <IconButton size="small" onClick={() => openWebhooks(partner)}><WebhookIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Rotate API key">
                      <IconButton size="small" onClick={() => rotateKey(partner)}><KeyIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Partner' : 'Add Partner'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box display="flex" gap={2}>
              <TextField label="Internal Name" value={form.name} onChange={setField('name')} required fullWidth size="small" />
              <TextField label="Code (slug)" value={form.code} onChange={setField('code')} required fullWidth size="small"
                disabled={!!editingId} helperText={editingId ? 'Code cannot change' : 'e.g. acme-insurance'} />
            </Box>
            <Typography variant="subtitle2">Customer-facing brand</Typography>
            <TextField label="Display Name (what customers see)" value={form.displayName} onChange={setField('displayName')} required fullWidth size="small" />
            <Box display="flex" gap={2} alignItems="center">
              <TextField label="Primary Color" type="color" value={form.primaryColor} onChange={setField('primaryColor')} sx={{ width: 140 }} size="small" />
              <TextField label="Secondary Color" type="color" value={form.secondaryColor} onChange={setField('secondaryColor')} sx={{ width: 140 }} size="small" />
              <FormControlLabel
                control={<Checkbox checked={form.autoDispatch} onChange={setField('autoDispatch')} />}
                label="Auto-dispatch incoming cases"
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField label="Support Phone (shown to customers)" value={form.supportPhone} onChange={setField('supportPhone')} fullWidth size="small" />
              <TextField label="Support Email" value={form.supportEmail} onChange={setField('supportEmail')} fullWidth size="small" />
            </Box>
            <Typography variant="subtitle2">Billing terms (monthly statements)</Typography>
            <Box display="flex" gap={2}>
              <TextField label="Markup %" type="number" value={form.markupPercent} onChange={setField('markupPercent')}
                fullWidth size="small" helperText="Charge = provider cost + markup" />
              <TextField label="GOA Fee ($)" type="number" value={form.goaFee} onChange={setField('goaFee')}
                fullWidth size="small" helperText="Flat; 0 = cost + markup" />
              <TextField label="Cancellation Fee ($)" type="number" value={form.cancellationFee} onChange={setField('cancellationFee')}
                fullWidth size="small" helperText="Flat; 0 = no charge" />
            </Box>
            <Typography variant="subtitle2">Partner contact (internal)</Typography>
            <Box display="flex" gap={2}>
              <TextField label="Name" value={form.contactName} onChange={setField('contactName')} fullWidth size="small" />
              <TextField label="Email" value={form.contactEmail} onChange={setField('contactEmail')} fullWidth size="small" />
              <TextField label="Phone" value={form.contactPhone} onChange={setField('contactPhone')} fullWidth size="small" />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}
            disabled={saving || !form.name || !form.code || !form.displayName}>
            {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Partner'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Shown-once API key dialog */}
      <Dialog open={!!issuedKey} onClose={() => setIssuedKey(null)} maxWidth="sm" fullWidth>
        <DialogTitle>API Key for {issuedKey?.partnerName}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This key is shown <strong>only once</strong>. Copy it now and deliver it to the partner securely.
          </Alert>
          <Box display="flex" alignItems="center" gap={1} sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography fontFamily="monospace" sx={{ wordBreak: 'break-all', flex: 1 }}>{issuedKey?.apiKey}</Typography>
            <IconButton onClick={() => copyText(issuedKey?.apiKey)}><CopyIcon /></IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIssuedKey(null)}>I've saved the key</Button>
        </DialogActions>
      </Dialog>

      {/* SLA scorecard + statement dialog */}
      <Dialog open={!!metricsPartner} onClose={() => setMetricsPartner(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Scorecard — {metricsPartner?.name} (last 30 days)</DialogTitle>
        <DialogContent>
          {!metrics ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={1.5} mt={1}>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip label={`${metrics.jobs.total} jobs`} />
                <Chip label={`${metrics.jobs.completed} completed`} color="success" />
                <Chip label={`${metrics.jobs.active} active`} color="info" />
                <Chip label={`${metrics.jobs.goa} GOA`} />
                <Chip label={`${metrics.jobs.canceled} canceled`} />
              </Box>
              <Typography variant="body2">
                <strong>Completion rate:</strong> {metrics.completionRatePercent ?? '—'}%
              </Typography>
              <Typography variant="body2">
                <strong>Avg time to dispatch:</strong> {metrics.avgTimeToDispatchMinutes ?? '—'} min
                &nbsp;·&nbsp;<strong>to arrival:</strong> {metrics.avgTimeToArrivalMinutes ?? '—'} min
              </Typography>
              <Typography variant="body2">
                <strong>ETA performance:</strong> {metrics.eta.onTimePercent ?? '—'}% on time
                ({metrics.eta.samples} samples, avg {metrics.eta.avgDeltaMinutes ?? '—'} min vs promise)
              </Typography>
              <Typography variant="body2">
                <strong>Customer satisfaction:</strong> {metrics.satisfaction.avgRating ?? '—'}/5
                ({metrics.satisfaction.responses} responses)
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Monthly billing statement (CSV)</Typography>
              <Box display="flex" gap={1} alignItems="center">
                <TextField type="month" size="small" value={statementMonth}
                  onChange={(event) => setStatementMonth(event.target.value)} />
                <Button variant="outlined" startIcon={<StatementIcon />}
                  onClick={() => downloadStatement(metricsPartner)}>
                  Download
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetricsPartner(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Per-partner webhook dialog */}
      <Dialog open={!!webhookPartner} onClose={() => setWebhookPartner(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Webhooks — {webhookPartner?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Endpoints here receive job events for <strong>this partner's cases only</strong> (signed; see WEBHOOKS.md).
          </Typography>
          {webhookSecret && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Signing secret (shown once):
              <Box display="flex" alignItems="center" gap={1}>
                <Typography fontFamily="monospace" sx={{ wordBreak: 'break-all', flex: 1 }}>{webhookSecret}</Typography>
                <IconButton size="small" onClick={() => copyText(webhookSecret)}><CopyIcon fontSize="small" /></IconButton>
              </Box>
            </Alert>
          )}
          {webhooks.map((hook) => (
            <Box key={hook._id} display="flex" alignItems="center" justifyContent="space-between" sx={{ py: 0.5 }}>
              <Box>
                <Typography variant="body2" fontFamily="monospace">{hook.url}</Typography>
                <Typography variant="caption" color={hook.active ? 'success.main' : 'text.secondary'}>
                  {hook.active ? 'Active' : 'Disabled'}
                </Typography>
              </Box>
              <Button size="small" color="error" onClick={() => removeWebhook(hook)}>Delete</Button>
            </Box>
          ))}
          <Box display="flex" gap={1} mt={2}>
            <TextField label="Endpoint URL" placeholder="https://partner.example.com/webhooks" value={webhookUrl}
              onChange={(event) => setWebhookUrl(event.target.value)} fullWidth size="small" />
            <Button variant="contained" onClick={addWebhook} disabled={webhookBusy || !webhookUrl}>Add</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWebhookPartner(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Partners;
